import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  CircularProgress,
  Box,
  TextField,
  IconButton,
  Button,
  Chip,
  Snackbar,
  MenuItem,
  Alert,
  Avatar,
  Paper,
  Select,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import MicIcon from "@mui/icons-material/Mic";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BlockIcon from "@mui/icons-material/Block";
import HomeIcon from "@mui/icons-material/Home";
import BuildIcon from "@mui/icons-material/Build";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TopNavBar from "../components/TopNavBar";
import { getDeviceData, updateDeviceField } from "../api/devices";
import { getLogs } from "../api/logs";
import {
  getDeviceSchedules,
  getLatestDeviceOperations,
} from "../api/deviceOperations";
import { getRcRouters } from "../api/rcDevices";
import { getCleaningPerformanceRows } from "../api/performance";
import { useAuth } from "../context/AuthContext";
import useSSESubscription from "../hooks/useSSESubscription";
import DeviceDocuments from "../components/DeviceDocuments";
import DeviceAssignmentModal from "../components/DeviceAssignmentModal";
import { formatHundredths } from "../utils/conversion.ts";

const OPERATION_REASON_LABELS = {
  NOT_RETURNED_TO_CHARGER: "Not returned to charger",
  NOT_COMPLETED_CONSECUTIVE_RED: "Missed scheduled cleanings",
  NOT_COMPLETED_CONSECUTIVE_YELLOW: "Missed scheduled cleanings",
  UNDER_50_LAST_5_PLANNED_RED: "<50% completed in last 5 planned days",
  UNDER_50_LAST_5_PLANNED_YELLOW: "<50% completed in last 5 planned days",
  LATEST_AREA_UNDER_30: "<30% area cleaned",
  OK: "No warnings",
};

const OPERATION_STATE_STYLES = {
  RED: { bg: "#fee2e2", text: "#b91c1c" },
  YELLOW: { bg: "#fef3c7", text: "#92400e" },
  GREEN: { bg: "#dcfce7", text: "#166534" },
};

export default function DevicePage() {
  const { user } = useAuth();
  const username = user?.username;
  const navigate = useNavigate();
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editingField, setEditingField] = useState(null);
  const [fieldValue, setFieldValue] = useState("");
  const [routerOptions, setRouterOptions] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [schedulesError, setSchedulesError] = useState(null);
  const [schedulesLoaded, setSchedulesLoaded] = useState(false);
  const [performanceRows, setPerformanceRows] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState(null);
  const [performanceHasMore, setPerformanceHasMore] = useState(true);
  const [performanceLoaded, setPerformanceLoaded] = useState(false);
  const [latestOperation, setLatestOperation] = useState(null);
  const [latestOperationLoading, setLatestOperationLoading] = useState(false);
  const [latestOperationError, setLatestOperationError] = useState(null);
  const [latestOperationLoaded, setLatestOperationLoaded] = useState(false);
  const [deviceLogs, setDeviceLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [logsHasMore, setLogsHasMore] = useState(true);
  const [logsLoaded, setLogsLoaded] = useState(false);

  const { deviceId } = useParams();

  // SSE message handler for real-time device updates
  const handleSSEMessage = useCallback(
    (message) => {
      if (
        message.type === "robot_status_updated" &&
        message.entityId === deviceId
      ) {
        console.log("DevicePage received SSE update:", message);

        // Extract latestLog data from the SSE message
        const latestLog =
          message.updateData?.latestLog ||
          message.data?.latestLog ||
          message.latestLog;

        const deviceDataUpdate =
          message.updateData?.device || message.data?.device || message.device;

        console.log("Extracted latestLog for DevicePage:", latestLog);
        console.log("Extracted deviceData for DevicePage:", deviceDataUpdate);

        if (latestLog && deviceData) {
          // Update the device data state with the real-time log data
          setDeviceData((prevData) => ({
            ...prevData,
            // Update all latestLog fields
            ...latestLog,
            // Also update device connection data if available
            connected: deviceDataUpdate?.connected ?? prevData?.connected,
            last_update: deviceDataUpdate?.last_update ?? prevData?.last_update,
          }));

          console.log(
            `🔄 [DevicePage] Updated device ${deviceId} with real-time data`,
          );
        }
      }
    },
    [deviceId, deviceData],
  );

  // SSE subscription for real-time updates
  const { isConnected: sseConnected, error: sseError } = useSSESubscription(
    "devicePage",
    handleSSEMessage,
    [deviceId, deviceData],
  );

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Helper function to check if a value should be displayed
  const shouldDisplayValue = (value) => {
    return (
      value !== null &&
      value !== undefined &&
      value !== "n/a" &&
      value !== "N/A" &&
      value !== "" &&
      String(value).toLowerCase() !== "null"
    );
  };

  // Helper function to format field names
  const formatFieldName = (fieldName) => {
    return fieldName
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace(/Id/g, "ID")
      .replace(/Pk/g, "PK")
      .replace(/Uk/g, "UK")
      .replace(/Rc/g, "RC")
      .replace(/Tb/g, "TB");
  };

  // Helper function to format values
  const formatValue = (value, fieldName, deviceData = null) => {
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    if (
      fieldName.includes("timestamp") ||
      fieldName.includes("created_at") ||
      fieldName.includes("updated_at")
    ) {
      return new Date(value).toLocaleString();
    }
    // Area fields are stored in hundredths of m²; present as m².
    if (
      /_area$/.test(fieldName) &&
      typeof value === "number" &&
      Number.isFinite(value)
    ) {
      return formatHundredths(value);
    }
    if (fieldName === "battery_level") {
      return `${value}%`;
    }
    if (fieldName === "response_codes") {
      try {
        const codes = JSON.parse(value);
        return (
          codes.map((code) => `${code.code}: ${code.description}`).join(", ") ||
          null
        );
      } catch {
        return value;
      }
    }
    return String(value);
  };

  // Group fields by category
  const categorizeFields = (data) => {
    const categories = {
      "Device Information": {
        "Basic Device Info": [],
        "Device Identification": [],
        "Device Settings": [],
        "Device Timestamps": [],
      },
      "Current Status": {
        "Operational Status": [],
        "System Status": [],
        "Position & Navigation": [],
        "Hardware Status": [],
      },
      Configuration: {
        "Cleaning Settings": [],
        "Audio & Visual": [],
        "Operation Modes": [],
        Scheduling: [],
      },
      "Customer & Location": {
        "Customer Information": [],
        "Location Details": [],
        "Building & Room": [],
      },
      "Version Information": [],
    };

    const categoryMappings = {
      // Basic device identification and hardware info
      "Basic Device Info": [
        "device_name",
        "manufacturer",
        "model",
        "model_number",
        "mac_address",
        "router",
      ],
      "Device Identification": ["uk_device_id", "rc_device_id", "owner_id"],
      "Device Settings": [
        "device_active",
        "polling",
        "device_room_id",
        "device_location_id",
      ],
      "Device Timestamps": ["device_created_at", "device_updated_at"],

      // Operational state and activity
      "Operational Status": [
        "operating_state",
        "movement_state",
        "cleaning_mode",
        "cleaning_step",
        "cleaning_type",
        "cleaning_area",
        "cleaning_extent",
        "remaining_time",
      ],
      "System Status": [
        "battery_level",
        "dust_bag_status",
        "station_filter_status",
        "motor_filter_status",
        "microphone_state",
      ],
      "Position & Navigation": [
        "current_position",
        "near_object",
        "blocking_status",
        "homing_reason",
      ],
      "Hardware Status": [
        "handlebar_status",
        "state_code",
        "response_codes",
        "log_timestamp",
      ],

      // Configuration and settings
      "Cleaning Settings": ["water_spray_level", "turbo_mode"],
      "Audio & Visual": ["volume_level", "sound_mode", "brightness_level"],
      "Operation Modes": ["driving_mode", "repeat_mode", "map_based_operation"],
      Scheduling: [
        "do_not_disturb",
        "start_time",
        "end_time",
        "config_created_at",
      ],

      // Location and organizational info
      "Customer Information": [
        "customer_name",
        "customer_address",
        "customer_active",
      ],
      "Location Details": [
        "location_name",
        "location_address",
        "location_type_name",
        "number_of_buildings",
      ],
      "Building & Room": [
        "building_name",
        "department_name",
        "department_floor",
        "room_name",
        "room_area",
        "room_cleaning_area",
        "room_baseline_cleaning_area",
      ],

      // Software and firmware versions
      "Version Information": [
        "firmware_version",
        "software_version",
        "driver_version",
        "spec_version",
        "release_year",
        "new_version_available",
        "complete_versions",
        "version_updated_at",
      ],
    };

    Object.entries(data).forEach(([key, value]) => {
      if (shouldDisplayValue(value) || key === "mac_address") {
        for (const [categoryName, fields] of Object.entries(categoryMappings)) {
          if (fields.includes(key)) {
            // Find which parent category this subcategory belongs to
            for (const [parentName, parentData] of Object.entries(categories)) {
              if (Array.isArray(parentData)) {
                // Version Information is flat
                if (
                  parentName === "Version Information" &&
                  categoryName === "Version Information"
                ) {
                  categories[parentName].push({ key, value });
                  break;
                }
              } else {
                // Nested categories
                if (parentData[categoryName]) {
                  categories[parentName][categoryName].push({ key, value });
                  break;
                }
              }
            }
            break;
          }
        }
      }
    });

    // Remove empty subcategories and parent categories
    Object.keys(categories).forEach((parentCategory) => {
      if (Array.isArray(categories[parentCategory])) {
        // Handle flat categories (Version Information)
        if (categories[parentCategory].length === 0) {
          delete categories[parentCategory];
        }
      } else {
        // Handle nested categories
        Object.keys(categories[parentCategory]).forEach((subCategory) => {
          if (categories[parentCategory][subCategory].length === 0) {
            delete categories[parentCategory][subCategory];
          }
        });
        // Remove parent if no subcategories remain
        if (Object.keys(categories[parentCategory]).length === 0) {
          delete categories[parentCategory];
        }
      }
    });

    return categories;
  };

  // Handler for assign device button
  const handleAssignDevice = async () => {
    console.log("Assign device clicked");
    setAssignModalOpen(true);
  };

  // Handler for assignment success
  const handleAssignmentSuccess = (result, hierarchyData) => {
    showSnackbar("Device successfully assigned!", "success");
    // Refresh device data to show updated assignment
    fetchDeviceData();
  };

  // Handler for assignment error
  const handleAssignmentError = (errorMessage) => {
    showSnackbar(errorMessage, "error");
  };

  // Handler for closing assignment modal
  const handleCloseAssignmentModal = () => {
    setAssignModalOpen(false);
  };

  // Handler for move robot button
  const handleMoveRobot = () => {
    console.log("Move robot clicked");
    showSnackbar("Move Robot functionality will be implemented soon", "info");
  };

  // Registry of device fields that support inline editing; add an entry here to
  // make another field editable without touching the handlers below.
  const EDITABLE_FIELDS = {
    mac_address: {
      label: "MAC Address",
      formatInput: (value) => {
        const cleaned = value.replace(/[^0-9A-Fa-f]/g, "").toUpperCase();
        const formatted = cleaned.match(/.{1,2}/g)?.join(":") || cleaned;
        return formatted.substring(0, 17);
      },
      validate: (value) =>
        /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(value)
          ? null
          : "Invalid MAC address format",
      inputProps: { maxLength: 17 },
      placeholder: "XX:XX:XX:XX:XX:XX",
    },
    router_id: {
      label: "Router",
      type: "select",
    },
  };

  const startEditField = (fieldKey) => {
    const config = EDITABLE_FIELDS[fieldKey] || {};
    setFieldValue(deviceData?.[fieldKey] || "");
    setEditingField(fieldKey);
    if (config.type === "select") {
      loadRouterOptions();
    }
  };

  const loadRouterOptions = useCallback(async () => {
    if (routerOptions.length > 0) return;
    try {
      const rows = await getRcRouters();
      setRouterOptions(rows);
    } catch (err) {
      console.error("Error loading router options:", err.message);
    }
  }, [routerOptions.length]);

  const cancelEditField = () => {
    setEditingField(null);
    setFieldValue("");
  };

  const saveEditField = async () => {
    if (!editingField) return;

    const config = EDITABLE_FIELDS[editingField] || {};
    const value = config.formatInput
      ? config.formatInput(fieldValue)
      : fieldValue;
    const validationError = config.validate ? config.validate(value) : null;
    if (validationError) {
      showSnackbar(validationError, "error");
      return;
    }

    try {
      const result = await updateDeviceField(deviceId, editingField, value);
      if (result.success) {
        showSnackbar(
          `${config.label || formatFieldName(editingField)} updated successfully`,
          "success",
        );
        cancelEditField();
        fetchDeviceData();
      } else {
        showSnackbar(
          `Failed to update ${config.label || formatFieldName(editingField)}: ${result.error}`,
          "error",
        );
      }
    } catch (error) {
      showSnackbar(
        `Error updating ${config.label || formatFieldName(editingField)}: ${error.message}`,
        "error",
      );
    }
  };

  const fetchDeviceData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDeviceData(deviceId);
      setDeviceData(data);
      console.log("Device data:", data);
    } catch (err) {
      console.error("Error loading device data:", err.message);
      setError("Failed to load device data");
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchDeviceData();
  }, [fetchDeviceData]);

  useEffect(() => {
    const deviceName = deviceData?.device_name;

    if (deviceName) {
      document.title = `${deviceName} - RC Service Tool`;
      return;
    }

    document.title = "Device - RC Service Tool";
  }, [deviceData]);

  // Log SSE connection status
  useEffect(() => {
    if (sseError) {
      console.error("SSE connection error on DevicePage:", sseError);
    }
    if (sseConnected) {
      console.log("SSE connected on DevicePage for device:", deviceId);
    }
  }, [sseConnected, sseError, deviceId]);

  // Schedules are only fetched once the Schedules tab is opened, and only once per device.
  useEffect(() => {
    if (activeTab !== 3 || schedulesLoaded || !deviceData?.device_pk_id) {
      return;
    }

    let active = true;
    setSchedulesLoading(true);
    setSchedulesError(null);

    getDeviceSchedules(deviceData.device_pk_id)
      .then((rows) => {
        if (!active) return;
        setSchedules(rows);
        setSchedulesLoaded(true);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error loading device schedules:", err.message);
        setSchedulesError("Failed to load device schedules");
      })
      .finally(() => {
        if (active) setSchedulesLoading(false);
      });

    return () => {
      active = false;
    };
  }, [activeTab, deviceData?.device_pk_id, schedulesLoaded]);

  const PERFORMANCE_PAGE_SIZE = 10;

  const loadMorePerformanceRows = useCallback(async () => {
    if (
      !deviceData?.device_pk_id ||
      performanceLoading ||
      !performanceHasMore
    ) {
      return;
    }

    setPerformanceLoading(true);
    setPerformanceError(null);

    try {
      const result = await getCleaningPerformanceRows({
        devicePkId: deviceData.device_pk_id,
        limit: PERFORMANCE_PAGE_SIZE,
        offset: performanceRows.length,
      });
      const rows = Array.isArray(result?.rows) ? result.rows : [];
      setPerformanceRows((prev) => [...prev, ...rows]);
      setPerformanceHasMore(rows.length === PERFORMANCE_PAGE_SIZE);
    } catch (err) {
      console.error("Error loading device performance rows:", err.message);
      setPerformanceError("Failed to load performance history");
    } finally {
      setPerformanceLoading(false);
    }
  }, [
    deviceData?.device_pk_id,
    performanceHasMore,
    performanceLoading,
    performanceRows.length,
  ]);

  // First page loads once the Performance tab is opened; further pages load on scroll.
  useEffect(() => {
    if (activeTab !== 4 || performanceLoaded || !deviceData?.device_pk_id) {
      return;
    }
    setPerformanceLoaded(true);
    loadMorePerformanceRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, deviceData?.device_pk_id, performanceLoaded]);

  // Loads once alongside the performance rows; there's at most one "latest" row per device.
  useEffect(() => {
    if (activeTab !== 4 || latestOperationLoaded || !deviceData?.device_pk_id) {
      return;
    }
    setLatestOperationLoaded(true);
    setLatestOperationLoading(true);
    setLatestOperationError(null);

    getLatestDeviceOperations({
      devicePkId: deviceData.device_pk_id,
      limit: 1,
    })
      .then((rows) => {
        setLatestOperation(
          Array.isArray(rows) && rows.length > 0 ? rows[0] : null,
        );
      })
      .catch((err) => {
        console.error("Error loading latest device operation:", err.message);
        setLatestOperationError(
          "Failed to load latest device operation status",
        );
      })
      .finally(() => setLatestOperationLoading(false));
  }, [activeTab, deviceData?.device_pk_id, latestOperationLoaded]);

  const handlePerformanceScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadMorePerformanceRows();
    }
  };

  const LOGS_INITIAL_LIMIT = 100;
  const LOGS_PAGE_SIZE = 50;

  const loadMoreDeviceLogs = useCallback(async () => {
    if (!deviceData?.uk_device_id || logsLoading || !logsHasMore) {
      return;
    }

    setLogsLoading(true);
    setLogsError(null);

    try {
      const pageLimit =
        deviceLogs.length === 0 ? LOGS_INITIAL_LIMIT : LOGS_PAGE_SIZE;
      const result = await getLogs({
        device_id: deviceData.uk_device_id,
        start: "1970-01-01 00:00:00",
        end: new Date().toISOString().slice(0, 19).replace("T", " "),
        limit: pageLimit,
        offset: deviceLogs.length,
      });
      const rows = Array.isArray(result?.rows) ? result.rows : [];
      setDeviceLogs((prev) => [...prev, ...rows]);
      setLogsHasMore(rows.length === pageLimit);
    } catch (err) {
      console.error("Error loading device logs:", err.message);
      setLogsError("Failed to load device logs");
    } finally {
      setLogsLoading(false);
    }
  }, [deviceData?.uk_device_id, deviceLogs.length, logsHasMore, logsLoading]);

  // First 100 logs load once the Current Status tab is shown; further pages load on scroll.
  useEffect(() => {
    if (activeTab !== 0 || logsLoaded || !deviceData?.uk_device_id) {
      return;
    }
    setLogsLoaded(true);
    loadMoreDeviceLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, deviceData?.uk_device_id, logsLoaded]);

  const handleLogsScroll = (event) => {
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      loadMoreDeviceLogs();
    }
  };

  const STATUS_FIELD_ICONS = {
    operating_state: SettingsSuggestIcon,
    movement_state: DirectionsWalkIcon,
    cleaning_mode: CleaningServicesIcon,
    cleaning_step: CleaningServicesIcon,
    cleaning_type: CleaningServicesIcon,
    cleaning_area: SquareFootIcon,
    cleaning_extent: SquareFootIcon,
    remaining_time: AccessTimeIcon,
    dust_bag_status: DeleteSweepIcon,
    station_filter_status: FilterAltIcon,
    motor_filter_status: FilterAltIcon,
    microphone_state: MicIcon,
    current_position: MyLocationIcon,
    near_object: WarningAmberIcon,
    blocking_status: BlockIcon,
    homing_reason: HomeIcon,
    handlebar_status: BuildIcon,
    state_code: InfoOutlinedIcon,
  };

  // battery_level and response_codes get value-dependent icons/colors rather than a fixed one.
  const getStatusFieldIconAndColor = (key, value) => {
    if (key === "battery_level") {
      const pct = Number(value);
      const low = Number.isFinite(pct) && pct <= 20;
      return {
        Icon: low ? BatteryAlertIcon : BatteryChargingFullIcon,
        color: low ? "error.main" : "success.main",
      };
    }
    if (key === "response_codes") {
      const hasCodes = (() => {
        try {
          const parsed = JSON.parse(value || "[]");
          return Array.isArray(parsed) && parsed.length > 0;
        } catch {
          return Boolean(value);
        }
      })();
      return {
        Icon: hasCodes ? ErrorOutlineIcon : InfoOutlinedIcon,
        color: hasCodes ? "error.main" : "text.secondary",
      };
    }
    return {
      Icon: STATUS_FIELD_ICONS[key] || InfoOutlinedIcon,
      color: "text.secondary",
    };
  };

  const renderCurrentStatusIcons = (fields) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
      {fields.map(({ key, value }) => {
        const { Icon, color } = getStatusFieldIconAndColor(key, value);
        return (
          <Box
            key={key}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              minWidth: 150,
            }}
          >
            <Icon sx={{ color, fontSize: 22, flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", fontWeight: 600, whiteSpace: "nowrap" }}
              >
                {formatFieldName(key)}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                {formatValue(value, key, deviceData)}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  if (loading) {
    return (
      <>
        <TopNavBar
          title="device - RC Service Tool"
          username={username}
          heading={"Device Page"}
        />
        <Container sx={{ mt: 4, textAlign: "center" }}>
          <CircularProgress />
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopNavBar
          title="device - RC Service Tool"
          username={username}
          heading={"Device Page"}
        />
        <Container sx={{ mt: 4 }}>
          <Typography color="error">{error}</Typography>
        </Container>
      </>
    );
  }

  if (!deviceData) {
    return (
      <>
        <TopNavBar
          title="device - RC Service Tool"
          username={username}
          heading={"Device Page"}
        />
        <Container sx={{ mt: 4 }}>
          <Typography>
            Device not found - check the Reformed Cleaning device ID is correct
          </Typography>
        </Container>
      </>
    );
  }

  const categorizedData = categorizeFields(deviceData);

  // Compact "Label: value" inline field, no border/box - used across the whole page
  const renderInlineField = (label, value, options = {}) => {
    const { editable, isEditing, editorNode, onEdit } = options;
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
        >
          {label}:
        </Typography>
        {isEditing ? (
          editorNode
        ) : (
          <Typography
            variant="caption"
            sx={{ wordBreak: "break-word", userSelect: "text" }}
          >
            {value}
          </Typography>
        )}
        {editable && !isEditing && (
          <IconButton size="small" onClick={onEdit} sx={{ p: 0.25 }}>
            <EditIcon sx={{ fontSize: 13 }} />
          </IconButton>
        )}
      </Box>
    );
  };

  // Single editor node for whichever field is currently being edited, driven by EDITABLE_FIELDS.
  const fieldEditorNode =
    editingField && EDITABLE_FIELDS[editingField] ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {EDITABLE_FIELDS[editingField].type === "select" ? (
          <Select
            size="small"
            value={fieldValue}
            onChange={(e) => setFieldValue(e.target.value)}
            displayEmpty
            sx={{ width: 180, fontSize: 12 }}
            autoFocus
          >
            <MenuItem value="">
              <em>Unassigned</em>
            </MenuItem>
            {routerOptions.map((router) => (
              <MenuItem key={router.pk_id} value={router.pk_id}>
                {router.name}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <TextField
            size="small"
            value={fieldValue}
            onChange={(e) => {
              const config = EDITABLE_FIELDS[editingField];
              const next = config.formatInput
                ? config.formatInput(e.target.value)
                : e.target.value;
              setFieldValue(next);
            }}
            placeholder={EDITABLE_FIELDS[editingField].placeholder}
            slotProps={{
              htmlInput: {
                ...(EDITABLE_FIELDS[editingField].inputProps || {}),
                style: { fontSize: 12, padding: "2px 6px" },
              },
            }}
            sx={{ width: 140 }}
            autoFocus
          />
        )}
        <IconButton
          size="small"
          onClick={saveEditField}
          sx={{ color: "success.main", p: 0.25 }}
        >
          <CheckIcon sx={{ fontSize: 14 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={cancelEditField}
          sx={{ color: "error.main", p: 0.25 }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    ) : null;

  // Reusable key/value field used across the Configuration and Location sections
  const renderFieldCard = (key, value) => {
    const isEditing = editingField === key;
    const editable = key in EDITABLE_FIELDS;

    return renderInlineField(
      formatFieldName(key),
      formatValue(value, key, deviceData),
      {
        editable,
        isEditing,
        editorNode: isEditing ? fieldEditorNode : undefined,
        onEdit: editable ? () => startEditField(key) : undefined,
      },
    );
  };

  const renderFieldGrid = (fields) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {fields.map(({ key, value }) => (
        <Box key={key}>{renderFieldCard(key, value)}</Box>
      ))}
    </Box>
  );

  const renderNestedCategory = (categoryData) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {Object.entries(categoryData).map(([subCategoryName, fields]) => (
        <Box
          key={subCategoryName}
          sx={{ p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}
        >
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, mb: 0.75, color: "primary.main" }}
          >
            {subCategoryName}
          </Typography>
          {renderFieldGrid(fields)}
        </Box>
      ))}
    </Box>
  );

  // Left panel: core identity fields shown directly, independent of the grouped categories
  const makeModelValue = [deviceData.manufacturer, deviceData.model]
    .filter(Boolean)
    .join(" / ");

  const currentStatusData = categorizedData["Current Status"] || {};
  const configurationData = categorizedData["Configuration"] || {};
  const locationData = categorizedData["Customer & Location"] || {};
  const versionData = categorizedData["Version Information"] || [];

  const allCurrentStatusFields = Object.values(currentStatusData).flat();
  const logTimestampEntry = allCurrentStatusFields.find(
    ({ key }) => key === "log_timestamp",
  );
  const statusTableFields = allCurrentStatusFields.filter(
    ({ key }) => key !== "log_timestamp",
  );

  const hasAssignmentGaps =
    !deviceData.customer_name ||
    !deviceData.location_name ||
    !deviceData.room_name;

  return (
    <>
      <TopNavBar
        title="device - RC Service Tool"
        username={username}
        heading={"Device Page"}
      />
      <Container
        sx={{ mt: 4, pb: 4, width: "90%", maxWidth: "1200px", mx: "auto" }}
      >
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h4">
            {deviceData.device_name || "Unnamed Device"}
          </Typography>
          {deviceData.manufacturer && (
            <Chip
              label={deviceData.manufacturer.toUpperCase()}
              color="primary"
              variant="outlined"
            />
          )}
          {deviceData.model && (
            <Chip
              label={deviceData.model}
              color="secondary"
              variant="outlined"
            />
          )}
        </Box>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
          {deviceData.customer_name ? (
            <Chip
              label={deviceData.customer_name.toUpperCase()}
              color="success"
              variant="outlined"
              avatar={
                <Avatar>
                  <PersonIcon />
                </Avatar>
              }
            />
          ) : (
            <Chip label="No Customer" color="default" variant="outlined" />
          )}
          {(!deviceData.customer_name ||
            !deviceData.location_name ||
            !deviceData.room_name) && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleAssignDevice}
            >
              Assign Device
            </Button>
          )}
          {deviceData.manufacturer === "lionsbot" && (
            <Button
              variant="contained"
              color="primary"
              onClick={() =>
                navigate(`/device/${deviceId}/cleaning-configuration`)
              }
            >
              Cleaning Configuration
            </Button>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Device Info
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                  {renderInlineField("Name", deviceData.device_name || "-")}
                  {renderInlineField("PKid", deviceData.device_pk_id || "-")}
                </Box>
                {shouldDisplayValue(deviceData.rc_device_id) && (
                  <Box>
                    {renderInlineField("UUID", deviceData.rc_device_id)}
                  </Box>
                )}
                {shouldDisplayValue(deviceData.uk_device_id) && (
                  <Box>{renderInlineField("S/N", deviceData.uk_device_id)}</Box>
                )}
                {shouldDisplayValue(deviceData.account) && (
                  <Box>{renderInlineField("Account", deviceData.account)}</Box>
                )}

                <Box sx={{ display: "flex", flexDirection: "row", gap: 0.75 }}>
                  {renderInlineField("Active", deviceData.device_active)}
                  {renderInlineField("Polling", deviceData.polling)}
                </Box>

                <Box>
                  {renderFieldCard("mac_address", deviceData.mac_address)}
                </Box>
                <Box>
                  {renderFieldCard(
                    "router_id",
                    deviceData.router_name || deviceData.router_id,
                  )}
                </Box>
                {makeModelValue && (
                  <>
                    {shouldDisplayValue(deviceData.manufacturer) && (
                      <Box>
                        {renderInlineField(
                          "Manufacturer",
                          deviceData.manufacturer,
                        )}
                      </Box>
                    )}
                    {shouldDisplayValue(deviceData.model) && (
                      <Box>{renderInlineField("Model", deviceData.model)}</Box>
                    )}
                  </>
                )}
                {shouldDisplayValue(deviceData.model_number) && (
                  <Box>
                    {renderInlineField("Model #", deviceData.model_number)}
                  </Box>
                )}
              </Box>
            </Paper>

            <Box sx={{ mt: 3 }}>
              <DeviceDocuments
                deviceId={deviceId}
                user={user}
                autoLoad={true}
                alwaysExpanded
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <Paper variant="outlined">
              <Tabs
                value={activeTab}
                onChange={(_, nextTab) => setActiveTab(nextTab)}
                sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
              >
                <Tab label="Current Status" />
                <Tab label="Configuration" />
                <Tab label="Location" />
                <Tab label="Schedules" />
                <Tab label="Performance" />
              </Tabs>

              <Box sx={{ p: 3 }}>
                {activeTab === 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 0.75 }}
                    >
                      Device Status
                    </Typography>
                    {logTimestampEntry && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Log Timestamp:{" "}
                        {formatValue(
                          logTimestampEntry.value,
                          logTimestampEntry.key,
                          deviceData,
                        )}
                      </Typography>
                    )}
                    {statusTableFields.length > 0 ? (
                      renderCurrentStatusIcons(statusTableFields)
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No current status data available.
                      </Typography>
                    )}

                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mt: 1, mb: 0.75 }}
                    >
                      Log History
                    </Typography>
                    {logsError && (
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {logsError}
                      </Typography>
                    )}
                    {!logsLoading && deviceLogs.length === 0 && !logsError && (
                      <Typography variant="body2" color="text.secondary">
                        No logs found for this device.
                      </Typography>
                    )}
                    {deviceLogs.length > 0 && (
                      <TableContainer
                        variant="outlined"
                        component={Paper}
                        onScroll={handleLogsScroll}
                        sx={{ maxHeight: 480 }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Timestamp
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Operating State
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Movement State
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Battery
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Charging
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Cleaning Step
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Homing Reason
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Response Codes
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {deviceLogs.map((log) => (
                              <TableRow key={log.pk_id}>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>
                                  {log.timestamp
                                    ? new Date(log.timestamp).toLocaleString()
                                    : "–"}
                                </TableCell>
                                <TableCell>
                                  {log.operating_state || "–"}
                                </TableCell>
                                <TableCell>
                                  {log.movement_state || "–"}
                                </TableCell>
                                <TableCell>
                                  {log.battery_level != null
                                    ? `${log.battery_level}%`
                                    : "–"}
                                </TableCell>
                                <TableCell>
                                  {Number(log.is_charging) === 1 ? "Yes" : "No"}
                                </TableCell>
                                <TableCell>
                                  {log.cleaning_step || "–"}
                                </TableCell>
                                <TableCell>
                                  {log.homing_reason || "–"}
                                </TableCell>
                                <TableCell>
                                  {formatValue(
                                    log.response_codes,
                                    "response_codes",
                                    log,
                                  ) || "–"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        {logsLoading && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              py: 1.5,
                            }}
                          >
                            <CircularProgress size={20} />
                          </Box>
                        )}
                      </TableContainer>
                    )}
                    {logsLoading && deviceLogs.length === 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Box>
                )}

                {activeTab === 1 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Configuration
                    </Typography>
                    {Object.keys(configurationData).length > 0 &&
                      renderNestedCategory(configurationData)}
                    {versionData.length > 0 && (
                      <Box
                        sx={{
                          mt: Object.keys(configurationData).length > 0 ? 2 : 0,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            mb: 0.75,
                            color: "primary.main",
                          }}
                        >
                          Version Information
                        </Typography>
                        <Box
                          sx={{
                            p: 1.5,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                          }}
                        >
                          {renderFieldGrid(versionData)}
                        </Box>
                      </Box>
                    )}
                    {Object.keys(configurationData).length === 0 &&
                      versionData.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No configuration data available.
                        </Typography>
                      )}
                  </Box>
                )}

                {activeTab === 2 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Location
                    </Typography>
                    {Object.keys(locationData).length > 0 ? (
                      renderNestedCategory(locationData)
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No location data available.
                      </Typography>
                    )}
                    <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAssignDevice}
                      >
                        {hasAssignmentGaps ? "Assign Device" : "Move Robot"}
                      </Button>
                    </Box>
                  </Box>
                )}

                {activeTab === 3 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Schedules
                    </Typography>
                    {schedulesLoading && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                    {schedulesError && (
                      <Typography variant="body2" color="error">
                        {schedulesError}
                      </Typography>
                    )}
                    {!schedulesLoading &&
                      !schedulesError &&
                      schedules.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No schedules found for this device.
                        </Typography>
                      )}
                    {!schedulesLoading && schedules.length > 0 && (
                      <TableContainer variant="outlined" component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Schedule ID
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Type
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Enabled
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Days
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Start
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                End
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Timezone
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Repeat Mode
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Cleaning Mode
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Rooms/Areas
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Cleaning Type
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Suction Level
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Water Level
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                API Timestamp
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {schedules.map((schedule) => {
                              const days = Array.isArray(schedule.day_of_week)
                                ? schedule.day_of_week
                                : (() => {
                                    try {
                                      const parsed = JSON.parse(
                                        schedule.day_of_week || "[]",
                                      );
                                      return Array.isArray(parsed)
                                        ? parsed
                                        : [];
                                    } catch {
                                      return [];
                                    }
                                  })();

                              return (
                                <TableRow key={schedule.pk_id}>
                                  <TableCell>{schedule.schedule_id}</TableCell>
                                  <TableCell>
                                    {schedule.schedule_type}
                                  </TableCell>
                                  <TableCell>
                                    {Number(schedule.enabled) === 1
                                      ? "Yes"
                                      : "No"}
                                  </TableCell>
                                  <TableCell>
                                    {days.length > 0 ? days.join(", ") : "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.start_time || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.end_time || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.timezone || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.repeat_mode || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.cleaning_mode || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.area_ids || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.cleaning_type || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.suction_level || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.water_spray_level || "–"}
                                  </TableCell>
                                  <TableCell>
                                    {schedule.api_timestamp || "–"}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                )}

                {activeTab === 4 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Performance
                    </Typography>
                    {latestOperationLoading && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress size={18} />
                      </Box>
                    )}
                    {latestOperationError && (
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {latestOperationError}
                      </Typography>
                    )}
                    {!latestOperationLoading && latestOperation && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          p: 1.5,
                          mb: 2,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 2,
                        }}
                      >
                        <Chip
                          label={latestOperation.state}
                          sx={{
                            fontWeight: 700,
                            bgcolor:
                              OPERATION_STATE_STYLES[latestOperation.state]?.bg,
                            color:
                              OPERATION_STATE_STYLES[latestOperation.state]
                                ?.text,
                          }}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {OPERATION_REASON_LABELS[
                              latestOperation.primary_reason_code
                            ] || latestOperation.primary_reason_code}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Calculated{" "}
                            {latestOperation.calculated_at
                              ? new Date(
                                  latestOperation.calculated_at,
                                ).toLocaleString()
                              : "–"}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {performanceError && (
                      <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                        {performanceError}
                      </Typography>
                    )}
                    {!performanceLoading &&
                      performanceRows.length === 0 &&
                      !performanceError && (
                        <Typography variant="body2" color="text.secondary">
                          No performance history found for this device.
                        </Typography>
                      )}
                    {performanceRows.length > 0 && (
                      <TableContainer
                        variant="outlined"
                        component={Paper}
                        onScroll={handlePerformanceScroll}
                        sx={{ maxHeight: 480 }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Start
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                End
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Duration (min)
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Room Area (m²)
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Area Cleaned (m²)
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                AUH %
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Scheduled
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Battery Used %
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Completed
                              </TableCell>
                              <TableCell sx={{ fontWeight: "bold" }}>
                                Error Reason
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {performanceRows.map((row) => {
                              const completedLabel =
                                Number(row.job_complete) === 1
                                  ? Number(row.job_complete_with_error) === 1
                                    ? "Yes (with error)"
                                    : "Yes"
                                  : "No";

                              return (
                                <TableRow key={row.pk_id}>
                                  <TableCell>
                                    {row.cleaning_start_time
                                      ? new Date(
                                          row.cleaning_start_time,
                                        ).toLocaleString()
                                      : "–"}
                                  </TableCell>
                                  <TableCell>
                                    {row.cleaning_end_time
                                      ? new Date(
                                          row.cleaning_end_time,
                                        ).toLocaleString()
                                      : "–"}
                                  </TableCell>
                                  <TableCell>
                                    {row.cleaning_duration_minutes ?? "–"}
                                  </TableCell>
                                  <TableCell>
                                    {row.room_area_minus_inventory_minus_things_on_floor ??
                                      "–"}
                                  </TableCell>
                                  <TableCell>
                                    {row.area_cleaned ?? "–"}
                                  </TableCell>
                                  <TableCell>
                                    {row.auh_percentage_cleaned != null
                                      ? `${row.auh_percentage_cleaned}%`
                                      : "–"}
                                  </TableCell>
                                  <TableCell>
                                    {Number(row.cleaning_scheduled) === 1
                                      ? "Yes"
                                      : "No"}
                                  </TableCell>
                                  <TableCell>
                                    {row.battery_pct_consumed != null
                                      ? `${row.battery_pct_consumed}%`
                                      : "–"}
                                  </TableCell>
                                  <TableCell>{completedLabel}</TableCell>
                                  <TableCell>
                                    {row.error_reason || "–"}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                        {performanceLoading && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              py: 1.5,
                            }}
                          >
                            <CircularProgress size={20} />
                          </Box>
                        )}
                      </TableContainer>
                    )}
                    {performanceLoading && performanceRows.length === 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 2,
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Device Assignment Modal */}
      <DeviceAssignmentModal
        open={assignModalOpen}
        onClose={handleCloseAssignmentModal}
        deviceId={deviceId}
        deviceName={deviceData?.device_name}
        onAssignmentSuccess={handleAssignmentSuccess}
        onAssignmentError={handleAssignmentError}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
